import { useState } from 'react';
import { toast } from 'sonner';
import { Unlock, KeySquare, Loader2 } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';

export const UserAccessActions = ({ u, canUnlock, onDone }) => {
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState('');
  const [busy, setBusy] = useState(false);

  const unlock = async () => {
    setBusy(true);
    try {
      await api.post(`/users/${u.user_id}/unlock`);
      toast.success(`${u.name || u.email} can sign in again`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not unlock');
    }
    setBusy(false);
  };

  const reset = async () => {
    if (pwd.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setBusy(true);
    try {
      await api.post(`/users/${u.user_id}/reset-password`, { new_password: pwd });
      toast.success(`New password set for ${u.email}. Share it with them privately.`);
      setOpen(false); setPwd('');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    }
    setBusy(false);
  };

  return (
    <>
      {u.locked_until && canUnlock && (
        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" title="Unlock sign-in"
          onClick={unlock} disabled={busy} data-testid={`unlock-user-${u.user_id}`}>
          <Unlock className="h-3.5 w-3.5" />
        </Button>
      )}
      <Button variant="ghost" size="icon" className="h-8 w-8" title="Set a new password"
        onClick={() => setOpen(true)} data-testid={`reset-pwd-user-${u.user_id}`}>
        <KeySquare className={`h-3.5 w-3.5 ${u.reset_requested_at ? 'text-amber-600' : ''}`} />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="reset-pwd-dialog">
          <DialogHeader>
            <DialogTitle>Set a new password</DialogTitle>
            <DialogDescription>
              For {u.name || u.email}. They're signed out everywhere and any sign-in lock is lifted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reset-pwd-input">New password</Label>
            <Input id="reset-pwd-input" type="text" value={pwd} onChange={(e) => setPwd(e.target.value)}
              placeholder="At least 8 characters" data-testid="reset-pwd-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="reset-pwd-cancel">Cancel</Button>
            <Button onClick={reset} disabled={busy} data-testid="reset-pwd-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Set password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const UserAccessBadges = ({ u }) => (
  <>
    {u.locked_until && (
      <span className="ml-2 rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-semibold text-destructive"
        data-testid={`locked-badge-${u.user_id}`}>Locked</span>
    )}
    {u.reset_requested_at && (
      <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800"
        data-testid={`reset-requested-badge-${u.user_id}`}>Wants new password</span>
    )}
  </>
);
